/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, IStackStyles, Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { CluMigrationContentSideMargin, CluMigrationSteps } from 'src/luis/verticals/cluMigrationModal/CluMigrationModal';
import { CluMigrationModalFooter } from 'src/luis/verticals/cluMigrationModal/components/CluMigrationModalFooter';

export type CluMigrationModalMigrationOverviewProps = {
    isValid?: boolean;
    onSubmit: () => void;
    onDismiss: () => void;
};

const messages = defineMessages({
    title: {
        id: 'cluMigration.migrationOverviewScreen.title',
        defaultMessage: 'Conversational language understanding'
    },
    description: {
        id: 'cluMigration.migrationOverviewScreen.description',
        defaultMessage: 'is the next generation service of LUIS. The service comes with several major improvements including:'
    },
    betterQualityMachineLearningTitle: {
        id: 'cluMigration.migrationOverviewScreen.betterQualityMachineLearningTitle',
        defaultMessage: 'Better quality machine learning'
    },
    betterQualityMachineLearningDescription: {
        id: 'cluMigration.migrationOverviewScreen.betterQualityMachineLearningDescription',
        defaultMessage:
            'using state-of-the-art technology that uses semantic understanding for intent classification and entity extraction. This new technology is robust against different variations of similar words, synonyms, and spelling mistakes.'
    },
    extensiveLanguageTitle: {
        id: 'cluMigration.migrationOverviewScreen.extensiveLanguageTitle',
        defaultMessage: 'Extensive language'
    },
    extensiveLanguageDescription: {
        id: 'cluMigration.migrationOverviewScreen.extensiveLanguageDescription',
        defaultMessage: 'support for up to 96 languages.'
    },
    multiLingualProjectsTitle: {
        id: 'cluMigration.migrationOverviewScreen.multiLingualProjectsTitle',
        defaultMessage: 'Multilingual projects'
    },
    multiLingualProjectsDescription: {
        id: 'cluMigration.migrationOverviewScreen.multiLingualProjectsDescription',
        defaultMessage: 'that allow you to train in one language and predict in others.'
    },
    integratedEvaluationTitle: {
        id: 'cluMigration.migrationOverviewScreen.integratedEvaluationTitle',
        defaultMessage: 'Integrated evaluation'
    },
    integratedEvaluationDescription: {
        id: 'cluMigration.migrationOverviewScreen.integratedEvaluationDescription',
        defaultMessage: 'to accelerate model improvements.'
    },
    title2: {
        id: 'cluMigration.migrationOverviewScreen.title2',
        defaultMessage: 'What happens when I migrate?'
    },
    subtitle1a: {
        id: 'cluMigration.migrationOverviewScreen.subtitle1a',
        defaultMessage: 'Your LUIS applications and resources will'
    },
    notBeAffected: {
        id: 'cluMigration.migrationOverviewScreen.notBeAffected',
        defaultMessage: 'not be affected.'
    },
    subtitle1b: {
        id: 'cluMigration.migrationOverviewScreen.subtitle1b',
        defaultMessage: 'The applications will remain as is and'
    },
    noInterruption: {
        id: 'cluMigration.migrationOverviewScreen.noInterruption',
        defaultMessage: 'no interruption'
    },
    subtitle1c: {
        id: 'cluMigration.migrationOverviewScreen.subtitle1c',
        defaultMessage: 'of authoring or runtime services in LUIS will occur.'
    },
    subtitle2a: {
        id: 'cluMigration.migrationOverviewScreen.subtitle2a',
        defaultMessage: 'Migration will only'
    },
    copy: {
        id: 'cluMigration.migrationOverviewScreen.copy',
        defaultMessage: 'copy'
    },
    subtitle2b: {
        id: 'cluMigration.migrationOverviewScreen.subtitle2b',
        defaultMessage: 'the selected applications to conversational language understanding.'
    },
    subtitle3a: {
        id: 'cluMigration.migrationOverviewScreen.subtitle3a',
        defaultMessage:
            'You will be able to author, train, deploy, and test your copied LUIS applications in conversational language understanding. Check out the'
    },
    migrationGuide: {
        id: 'cluMigration.migrationOverviewScreen.migrationGuide',
        defaultMessage: 'migration guide'
    },
    subtitle3b: {
        id: 'cluMigration.migrationOverviewScreen.subtitle3b',
        defaultMessage: 'to better understand the differences and improvements to expect.'
    }
});

const containerStyles = classNamesFunction<IStackProps, IStackStyles>()({
    root: { minHeight: '500px', height: '100%', position: 'relative', overflowY: 'auto' }
});

export const MigrationOverviewScreen = observer((props: CluMigrationModalMigrationOverviewProps) => {
    const { isValid = true, onSubmit, onDismiss } = props;
    const { localize } = useLocalization();

    const renderListItem = (title: string, description: string) => (
        <LuisText>
            <LuisText fontWeight={600}>{title}</LuisText>
            &nbsp;
            <LuisText>{description}</LuisText>
        </LuisText>
    );

    const renderSubtitle1 = () => (
        <LuisText>
            <LuisText>{localize(messages.subtitle1a)}</LuisText>
            &nbsp;
            <LuisText fontWeight={600}>{localize(messages.notBeAffected)}</LuisText>
            &nbsp;
            <LuisText>{localize(messages.subtitle1b)}</LuisText>
            &nbsp;
            <LuisText fontWeight={600}>{localize(messages.noInterruption)}</LuisText>
            &nbsp;
            <LuisText>{localize(messages.subtitle1c)}</LuisText>
        </LuisText>
    );

    const renderSubtitle2 = () => (
        <LuisText>
            <LuisText>{localize(messages.subtitle2a)}</LuisText>
            &nbsp;
            <LuisText fontWeight={600}>{localize(messages.copy)}</LuisText>
            &nbsp;
            <LuisText>{localize(messages.subtitle2b)}</LuisText>
        </LuisText>
    );

    const renderSubtitle3 = () => (
        <LuisText>
            <LuisText>{localize(messages.subtitle3a)}</LuisText>
            &nbsp;
            <LuisText>
                <ExternalLink href="https://aka.ms/luis-migration-clu">{localize(messages.migrationGuide)}</ExternalLink>
            </LuisText>
            &nbsp;
            <LuisText>{localize(messages.subtitle3b)}</LuisText>
        </LuisText>
    );

    return (
        <Stack>
            <Stack styles={containerStyles} tokens={{ childrenGap: 15, padding: `0 0 5px ${CluMigrationContentSideMargin}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>
                        <ExternalLink href="https://aka.ms/clu-docs">{localize(messages.title)}</ExternalLink>
                        &nbsp;
                        {localize(messages.description)}
                    </LuisText>
                </Stack>
                <Stack tokens={{ childrenGap: 3, padding: '0 0 0 30px' }}>
                    <ul>
                        <li>
                            {renderListItem(
                                localize(messages.betterQualityMachineLearningTitle),
                                localize(messages.betterQualityMachineLearningDescription)
                            )}
                        </li>
                        <li>
                            {renderListItem(localize(messages.extensiveLanguageTitle), localize(messages.extensiveLanguageDescription))}
                        </li>
                        <li>
                            {renderListItem(
                                localize(messages.multiLingualProjectsTitle),
                                localize(messages.multiLingualProjectsDescription)
                            )}
                        </li>
                        <li>
                            {renderListItem(
                                localize(messages.integratedEvaluationTitle),
                                localize(messages.integratedEvaluationDescription)
                            )}
                        </li>
                    </ul>
                </Stack>
                <Stack tokens={{ childrenGap: 15, padding: '15px 0 0 0' }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title2)}
                    </LuisText>
                    {renderSubtitle1()}
                    {renderSubtitle2()}
                    {renderSubtitle3()}
                </Stack>
            </Stack>

            <CluMigrationModalFooter
                isValid={isValid}
                onNext={onSubmit}
                onDismiss={onDismiss}
                activeStep={CluMigrationSteps.MigrationOverview}
            />
        </Stack>
    );
});
